import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FormEvent, useState, useEffect } from "react";
import { useI18n } from "../../i18n/i18n";
import CurrencySelector from "../CurrencySelector";
import { useAuth } from "../AuthProvider";
import { AuthModal } from "../AuthModal";
import { useSubscription } from "../../hooks/useSubscription";
import {
  Download,
  Calculator,
  Home,
  TrendingUp,
  DollarSign,
  ArrowRight,
  ChevronDown,
} from "lucide-react";
import { generateMortgagePDF } from "../../lib/mortgagePdfExport";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { LanguageLink } from "../LanguageLink";

interface MortgageCalculatorState {
  loanAmount: number;
  interestRate: number;
  interestRateType: "fixed" | "variable";
  loanTerm: number;
  downPayment: number;
  propertyTax: number;
  homeInsurance: number;
  pmi: number;
  propertyLocation: "spain" | "other";
  propertyCondition: "new" | "used";
}

interface MonthlyPayment {
  principalAndInterest: number;
  propertyTax: number;
  homeInsurance: number;
  pmi: number;
  total: number;
}

interface TaxesAndExpenses {
  notary: number;
  registry: number;
  management: number;
  taxes: number;
  appraisal: number;
  total: number;
}

interface AmortizationEntry {
  month: number;
  payment: number;
  principal: number;
  interest: number;
  balance: number;
  totalInterest: number;
}

const MortgageCalculator = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  const { isSubscribed, loading: subscriptionLoading, error: subscriptionError } = useSubscription();
  const [currency, setCurrency] = useState<string>("EUR");
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<"login" | "signup">("login");
  const [isCalculating, setIsCalculating] = useState(false);

  const [formState, setFormState] = useState<MortgageCalculatorState>({
    loanAmount: 300000,
    interestRate: 3.5,
    interestRateType: "fixed",
    loanTerm: 30,
    downPayment: 60000,
    propertyTax: 3600,
    homeInsurance: 1200,
    pmi: 0,
    propertyLocation: "spain",
    propertyCondition: "used",
  });

  const [monthlyPayment, setMonthlyPayment] = useState<MonthlyPayment | null>(
    null,
  );
  const [taxesAndExpenses, setTaxesAndExpenses] = useState<TaxesAndExpenses | null>(
    null,
  );
  const [amortizationSchedule, setAmortizationSchedule] = useState<
    AmortizationEntry[]
  >([]);
  const [showFullSchedule, setShowFullSchedule] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  // Remove automatic redirect - let users see the form and redirect only on calculate button click

  const calculateMortgage = () => {
    const principal = formState.loanAmount - formState.downPayment;
    const monthlyRate = formState.interestRate / 100 / 12;
    const numberOfPayments = formState.loanTerm * 12;

    // Calculate monthly principal and interest
    const monthlyPI =
      (principal *
        (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments))) /
      (Math.pow(1 + monthlyRate, numberOfPayments) - 1);

    const monthlyPropertyTax = formState.propertyTax / 12;
    const monthlyInsurance = formState.homeInsurance / 12;
    const monthlyPMI = formState.pmi / 12;

    const totalMonthly =
      monthlyPI + monthlyPropertyTax + monthlyInsurance + monthlyPMI;

    setMonthlyPayment({
      principalAndInterest: monthlyPI,
      propertyTax: monthlyPropertyTax,
      homeInsurance: monthlyInsurance,
      pmi: monthlyPMI,
      total: totalMonthly,
    });

    // Generate amortization schedule
    const schedule: AmortizationEntry[] = [];
    let remainingBalance = principal;
    let totalInterestPaid = 0;

    for (let month = 1; month <= numberOfPayments; month++) {
      const interestPayment = remainingBalance * monthlyRate;
      const principalPayment = monthlyPI - interestPayment;
      remainingBalance -= principalPayment;
      totalInterestPaid += interestPayment;

      schedule.push({
        month,
        payment: monthlyPI,
        principal: principalPayment,
        interest: interestPayment,
        balance: Math.max(0, remainingBalance),
        totalInterest: totalInterestPaid,
      });

      if (remainingBalance <= 0) break;
    }

    setAmortizationSchedule(schedule);
  };

  const calculateTaxesAndExpenses = () => {
    const propertyValue = formState.loanAmount;
    
    let notary = 0;
    let registry = 0;
    let management = 0;
    let taxes = 0;
    let appraisal = 600; // Base appraisal cost
    
    if (formState.propertyLocation === "spain") {
      // Spanish calculations based on property value and condition
      if (formState.propertyCondition === "new") {
        // New property (subject to VAT)
        notary = Math.max(600, propertyValue * 0.0003); // Minimum €600, typically 0.03%
        registry = Math.max(400, propertyValue * 0.0002); // Minimum €400, typically 0.02%
        management = 300;
        taxes = propertyValue * 0.10; // 10% VAT on new properties
      } else {
        // Used property (subject to transfer tax)
        notary = Math.max(600, propertyValue * 0.0004); // Slightly higher for used
        registry = Math.max(400, propertyValue * 0.0002);
        management = 300;
        taxes = propertyValue * 0.06; // 6% transfer tax on used properties (varies by region)
      }
    } else {
      // Generic calculations for other countries
      notary = propertyValue * 0.001; // 0.1%
      registry = propertyValue * 0.0005; // 0.05%
      management = 500;
      taxes = propertyValue * 0.05; // 5% generic tax rate
    }
    
    const total = notary + registry + management + taxes + appraisal;
    
    setTaxesAndExpenses({
      notary,
      registry,
      management,
      taxes,
      appraisal,
      total,
    });
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (!user) {
      setAuthMode("login");
      setShowAuthModal(true);
      return;
    }

    if (!isSubscribed) {
      window.location.href = "/pricing";
      return;
    }

    setIsCalculating(true);
    calculateMortgage();
    calculateTaxesAndExpenses();
    setIsCalculating(false);
  };

  const handleExportResults = () => {
    // Check if still loading subscription status
    if (subscriptionLoading) {
      return; // Don't allow export while loading
    }

    if (!user) {
      setAuthMode("login");
      setShowAuthModal(true);
      return;
    }

    // If there's an error checking subscription, inform the user
    if (subscriptionError) {
      console.error('Cannot export: subscription check failed', subscriptionError);
      // You could show a toast notification here instead
      alert('Error verificando tu suscripción. Por favor, inténtalo de nuevo o contacta con soporte.');
      return;
    }

    if (!isSubscribed) {
      window.location.href = "/pricing";
      return;
    }

    if (monthlyPayment && amortizationSchedule.length > 0) {
      generateMortgagePDF(
        formState,
        monthlyPayment,
        amortizationSchedule,
        taxesAndExpenses,
        t,
        currency,
      );
    }
  };

  const formatCurrency = (amount: number) => {
    const formattedAmount = amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    
    if (currency === "USD") {
      return `$${formattedAmount}`;
    } else if (currency === "GBP") {
      return `£${formattedAmount}`;
    } else if (currency === "CHF") {
      return `CHF ${formattedAmount}`;
    } else {
      // EUR - symbol goes to the right
      return `${formattedAmount} €`;
    }
  };

  // Show loading state while checking subscription
  if (subscriptionLoading) {
    return (
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        <div className="text-center py-16">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#EA580C] mx-auto"></div>
          <p className="mt-4 text-neutral-600">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  // Allow all users to see the form - redirect happens on calculate button click

  return (
    <div className="container mx-auto px-4 md:px-6 max-w-6xl">
      <div className="text-center mb-10">
        <h1 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-4">
          {t("tools.mortgage.title")}
        </h1>
        <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
          {t("tools.mortgage.description")}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Calculator Form */}
        <Card className="bg-white rounded-xl shadow-lg border border-neutral-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-neutral-800 flex items-center">
                <Calculator className="mr-2 h-5 w-5 text-[#EA580C]" />
                {t("calculator.enterDetails")}
              </h2>
              <CurrencySelector
                value={currency}
                onChange={setCurrency}
              />
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Loan Amount Slider */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Label className="text-sm font-medium text-neutral-700">
                    {t("tools.mortgage.loanAmount")}:
                  </Label>
                  <Input
                    type="number"
                    value={formState.loanAmount}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      if (value >= 50000 && value <= 2000000) {
                        setFormState((prev) => ({
                          ...prev,
                          loanAmount: value,
                        }));
                      }
                    }}
                    onBlur={(e) => {
                      const value = Number(e.target.value);
                      if (value < 50000) {
                        setFormState((prev) => ({
                          ...prev,
                          loanAmount: 50000,
                        }));
                      } else if (value > 2000000) {
                        setFormState((prev) => ({
                          ...prev,
                          loanAmount: 2000000,
                        }));
                      }
                    }}
                    className="w-24 h-8 text-sm px-2"
                    min={50000}
                    max={2000000}
                    step={1000}
                  />
                  <span className="text-sm text-neutral-600">€</span>
                </div>
                <Slider
                  value={[formState.loanAmount]}
                  onValueChange={(value) =>
                    setFormState((prev) => ({
                      ...prev,
                      loanAmount: value[0],
                    }))
                  }
                  max={2000000}
                  min={50000}
                  step={10000}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-neutral-500 mt-1">
                  <span>{formatCurrency(50000)}</span>
                  <span>{formatCurrency(2000000)}</span>
                </div>
              </div>

              {/* Down Payment Slider */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Label className="text-sm font-medium text-neutral-700">
                    {t("tools.mortgage.downPayment")}:
                  </Label>
                  <Input
                    type="number"
                    value={formState.downPayment}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      const maxDownPayment = Math.floor(formState.loanAmount * 0.5);
                      if (value >= 0 && value <= maxDownPayment) {
                        setFormState((prev) => ({
                          ...prev,
                          downPayment: value,
                        }));
                      }
                    }}
                    onBlur={(e) => {
                      const value = Number(e.target.value);
                      const maxDownPayment = Math.floor(formState.loanAmount * 0.5);
                      if (value < 0) {
                        setFormState((prev) => ({
                          ...prev,
                          downPayment: 0,
                        }));
                      } else if (value > maxDownPayment) {
                        setFormState((prev) => ({
                          ...prev,
                          downPayment: maxDownPayment,
                        }));
                      }
                    }}
                    className="w-24 h-8 text-sm px-2"
                    min={0}
                    max={Math.floor(formState.loanAmount * 0.5)}
                    step={1000}
                  />
                  <span className="text-sm text-neutral-600">€</span>
                  <span className="text-sm text-neutral-500">
                    ({((formState.downPayment / formState.loanAmount) * 100).toFixed(1)}%)
                  </span>
                </div>
                <Slider
                  value={[formState.downPayment]}
                  onValueChange={(value) =>
                    setFormState((prev) => ({
                      ...prev,
                      downPayment: value[0],
                    }))
                  }
                  max={Math.floor(formState.loanAmount * 0.5)}
                  min={0}
                  step={5000}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-neutral-500 mt-1">
                  <span>{formatCurrency(0)}</span>
                  <span>{formatCurrency(Math.floor(formState.loanAmount * 0.5))}</span>
                </div>
              </div>

              {/* Loan Term Slider */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Label className="text-sm font-medium text-neutral-700">
                    {t("tools.mortgage.loanTerm")}:
                  </Label>
                  <Input
                    type="number"
                    value={formState.loanTerm}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      if (value >= 5 && value <= 40) {
                        setFormState((prev) => ({
                          ...prev,
                          loanTerm: value,
                        }));
                      }
                    }}
                    onBlur={(e) => {
                      const value = Number(e.target.value);
                      if (value < 5) {
                        setFormState((prev) => ({
                          ...prev,
                          loanTerm: 5,
                        }));
                      } else if (value > 40) {
                        setFormState((prev) => ({
                          ...prev,
                          loanTerm: 40,
                        }));
                      }
                    }}
                    className="w-16 h-8 text-sm"
                    min={5}
                    max={40}
                    step={1}
                  />
                  <span className="text-sm text-neutral-600">{t("tools.mortgage.years")}</span>
                </div>
                <Slider
                  value={[formState.loanTerm]}
                  onValueChange={(value) =>
                    setFormState((prev) => ({
                      ...prev,
                      loanTerm: value[0],
                    }))
                  }
                  max={40}
                  min={5}
                  step={1}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-neutral-500 mt-1">
                  <span>5 {t("tools.mortgage.years")}</span>
                  <span>40 {t("tools.mortgage.years")}</span>
                </div>
              </div>

              {/* Interest Rate and Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label
                    htmlFor="interestRate"
                    className="text-sm font-medium text-neutral-700"
                  >
                    {t("tools.mortgage.interestRate")} (%)
                  </Label>
                  <Input
                    id="interestRate"
                    type="number"
                    value={formState.interestRate || ''}
                    onChange={(e) => {
                      const value = e.target.value === '' ? 0 : Number(e.target.value);
                      setFormState((prev) => ({
                        ...prev,
                        interestRate: value,
                      }));
                    }}
                    className="mt-1"
                    min="0"
                    max="20"
                    step="0.1"
                    placeholder="0.0"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium text-neutral-700">
                    {t("tools.mortgage.interestRateType")}
                  </Label>
                  <Select
                    value={formState.interestRateType}
                    onValueChange={(value: "fixed" | "variable") =>
                      setFormState((prev) => ({
                        ...prev,
                        interestRateType: value,
                      }))
                    }
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">{t("tools.mortgage.fixedRate")}</SelectItem>
                      <SelectItem value="variable">{t("tools.mortgage.variableRate")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                <div>
                  <Label
                    htmlFor="propertyTax"
                    className="text-sm font-medium text-neutral-700"
                  >
                    {t("tools.mortgage.propertyTax")} (
                    {t("tools.mortgage.annual")})
                  </Label>
                  <Input
                    id="propertyTax"
                    type="number"
                    value={formState.propertyTax || ''}
                    onChange={(e) => {
                      const value = e.target.value === '' ? 0 : Number(e.target.value);
                      setFormState((prev) => ({
                        ...prev,
                        propertyTax: value,
                      }));
                    }}
                    className="mt-1"
                    min="0"
                    step="100"
                    placeholder="0"
                  />
                </div>

                <div>
                  <Label
                    htmlFor="homeInsurance"
                    className="text-sm font-medium text-neutral-700"
                  >
                    {t("tools.mortgage.homeInsurance")} (
                    {t("tools.mortgage.annual")})
                  </Label>
                  <Input
                    id="homeInsurance"
                    type="number"
                    value={formState.homeInsurance || ''}
                    onChange={(e) => {
                      const value = e.target.value === '' ? 0 : Number(e.target.value);
                      setFormState((prev) => ({
                        ...prev,
                        homeInsurance: value,
                      }));
                    }}
                    className="mt-1"
                    min="0"
                    step="100"
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Property Location and Condition */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-neutral-700">
                    {t("tools.mortgage.propertyLocation")}
                  </Label>
                  <Select
                    value={formState.propertyLocation}
                    onValueChange={(value: "spain" | "other") =>
                      setFormState((prev) => ({
                        ...prev,
                        propertyLocation: value,
                      }))
                    }
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="spain">{t("tools.mortgage.spain")}</SelectItem>
                      <SelectItem value="other">{t("tools.mortgage.otherCountries")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm font-medium text-neutral-700">
                    {t("tools.mortgage.propertyCondition")}
                  </Label>
                  <Select
                    value={formState.propertyCondition}
                    onValueChange={(value: "new" | "used") =>
                      setFormState((prev) => ({
                        ...prev,
                        propertyCondition: value,
                      }))
                    }
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">{t("tools.mortgage.newProperty")}</SelectItem>
                      <SelectItem value="used">{t("tools.mortgage.usedProperty")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label
                    htmlFor="pmi"
                    className="text-sm font-medium text-neutral-700"
                  >
                    {t("tools.mortgage.pmi")} ({t("tools.mortgage.annual")})
                  </Label>
                  <Input
                    id="pmi"
                    type="number"
                    value={formState.pmi || ''}
                    onChange={(e) => {
                      const value = e.target.value === '' ? 0 : Number(e.target.value);
                      setFormState((prev) => ({
                        ...prev,
                        pmi: value,
                      }));
                    }}
                    className="mt-1"
                    min="0"
                    step="100"
                    placeholder="0"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className={`w-full font-medium py-3 ${
                  !user
                    ? "bg-neutral-400 hover:bg-neutral-500 text-white cursor-pointer"
                    : user && !isSubscribed
                      ? "bg-yellow-500 hover:bg-yellow-600 text-white cursor-pointer"
                      : "bg-[#EA580C] hover:bg-[#C2410C] text-white"
                }`}
                disabled={isCalculating}
              >
                {isCalculating
                  ? t("common.loading")
                  : !user
                    ? t("common.signInRequired")
                    : user && !isSubscribed
                      ? t("tools.mortgage.subscriptionRequiredButton")
                      : t("tools.mortgage.calculate")}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Results */}
        <div className="space-y-6">
          {monthlyPayment ? (
            <>
              {/* Monthly Payment Breakdown */}
              <Card className="bg-white rounded-xl shadow-lg border border-neutral-200">
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold text-neutral-800 mb-4 flex items-center">
                    <DollarSign className="mr-2 h-5 w-5 text-[#EA580C]" />
                    {t("tools.mortgage.monthlyPayment")}
                  </h3>

                  <div className="bg-[#EA580C] text-white p-4 rounded-lg mb-4">
                    <div className="text-center">
                      <p className="text-sm opacity-90">
                        {t("tools.mortgage.totalMonthlyPayment")}
                      </p>
                      <p className="text-3xl font-bold">
                        {formatCurrency(monthlyPayment.total)}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-neutral-600">
                        {t("tools.mortgage.principalAndInterest")}
                      </span>
                      <span className="font-medium">
                        {formatCurrency(monthlyPayment.principalAndInterest)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-600">
                        {t("tools.mortgage.propertyTax")}
                      </span>
                      <span className="font-medium">
                        {formatCurrency(monthlyPayment.propertyTax)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-600">
                        {t("tools.mortgage.homeInsurance")}
                      </span>
                      <span className="font-medium">
                        {formatCurrency(monthlyPayment.homeInsurance)}
                      </span>
                    </div>
                    {monthlyPayment.pmi > 0 && (
                      <div className="flex justify-between">
                        <span className="text-neutral-600">
                          {t("tools.mortgage.pmi")}
                        </span>
                        <span className="font-medium">
                          {formatCurrency(monthlyPayment.pmi)}
                        </span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Loan Summary */}
              <Card className="bg-white rounded-xl shadow-lg border border-neutral-200">
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold text-neutral-800 mb-4 flex items-center">
                    <TrendingUp className="mr-2 h-5 w-5 text-[#EA580C]" />
                    {t("tools.mortgage.loanSummary")}
                  </h3>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-neutral-600">
                        {t("tools.mortgage.totalLoanAmount")}
                      </p>
                      <p className="text-lg font-semibold">
                        {formatCurrency(
                          formState.loanAmount - formState.downPayment,
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-neutral-600">
                        {t("tools.mortgage.totalInterest")}
                      </p>
                      <p className="text-lg font-semibold">
                        {formatCurrency(
                          amortizationSchedule[amortizationSchedule.length - 1]
                            ?.totalInterest || 0,
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-neutral-600">
                        {t("tools.mortgage.totalPayments")}
                      </p>
                      <p className="text-lg font-semibold">
                        {formatCurrency(
                          monthlyPayment.principalAndInterest *
                            formState.loanTerm *
                            12,
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-neutral-600">
                        {t("tools.mortgage.downPaymentPercent")}
                      </p>
                      <p className="text-lg font-semibold">
                        {(
                          (formState.downPayment / formState.loanAmount) *
                          100
                        ).toFixed(1)}
                        %
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Taxes and Expenses */}
              {taxesAndExpenses && (
                <Card className="bg-white rounded-xl shadow-lg border border-neutral-200">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold text-neutral-800 mb-4 flex items-center">
                      <DollarSign className="mr-2 h-5 w-5 text-[#EA580C]" />
                      {t("tools.mortgage.taxesAndExpenses")}
                    </h3>
                    <p className="text-sm text-neutral-600 mb-4">
                      {t("tools.mortgage.taxesAndExpensesDescription")}
                    </p>

                    {/* Purchase Expenses */}
                    <div className="mb-6">
                      <h4 className="text-lg font-semibold text-neutral-700 mb-3">
                        {t("tools.mortgage.purchaseExpenses")}
                      </h4>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-neutral-600">{t("tools.mortgage.notary")}</span>
                          <span className="font-medium">{formatCurrency(taxesAndExpenses.notary)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-neutral-600">{t("tools.mortgage.registry")}</span>
                          <span className="font-medium">{formatCurrency(taxesAndExpenses.registry)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-neutral-600">{t("tools.mortgage.management")}</span>
                          <span className="font-medium">{formatCurrency(taxesAndExpenses.management)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-neutral-600">{t("tools.mortgage.taxes")}</span>
                          <span className="font-medium">{formatCurrency(taxesAndExpenses.taxes)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Mortgage Expenses */}
                    <div className="mb-6">
                      <h4 className="text-lg font-semibold text-neutral-700 mb-3">
                        {t("tools.mortgage.mortgageExpenses")}
                      </h4>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-neutral-600">{t("tools.mortgage.appraisal")}</span>
                          <span className="font-medium">{formatCurrency(taxesAndExpenses.appraisal)}</span>
                        </div>
                      </div>
                      <p className="text-xs text-neutral-500 mt-2">
                        {t("tools.mortgage.appraisalNote")}
                      </p>
                    </div>

                    {/* Total */}
                    <div className="border-t border-neutral-200 pt-4">
                      <div className="flex justify-between text-lg font-semibold">
                        <span className="text-neutral-800">{t("tools.mortgage.totalExpenses")}</span>
                        <span className="text-[#EA580C]">{formatCurrency(taxesAndExpenses.total)}</span>
                      </div>
                    </div>

                    {/* Warning message */}
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
                      <p className="text-sm text-yellow-800">
                        <strong>⚠️ {t("common.note")}:</strong> {t("tools.mortgage.orientativeCalculationWarning")}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card className="bg-white rounded-xl shadow-lg border border-neutral-200">
              <CardContent className="p-6">
                <div className="text-center py-8">
                  <Calculator className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
                  <p className="text-neutral-500">
                    {t("tools.mortgage.calculateToSeeResults")}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Amortization Schedule */}
      {amortizationSchedule.length > 0 && (
        <Card className="bg-white rounded-xl shadow-lg border border-neutral-200 mt-8">
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-neutral-800">
                {t("tools.mortgage.amortizationSchedule")}
              </h3>
              <Button
                variant="outline"
                onClick={() => setShowFullSchedule(!showFullSchedule)}
                className="text-[#EA580C] border-[#EA580C] hover:bg-[#EA580C] hover:text-white"
              >
                {showFullSchedule
                  ? t("tools.mortgage.showLess")
                  : t("tools.mortgage.showAll")}
              </Button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-200">
                    <th className="text-left py-2 px-2">
                      {t("tools.mortgage.month")}
                    </th>
                    <th className="text-right py-2 px-2">
                      {t("tools.mortgage.payment")}
                    </th>
                    <th className="text-right py-2 px-2">
                      {t("tools.mortgage.principal")}
                    </th>
                    <th className="text-right py-2 px-2">
                      {t("tools.mortgage.interest")}
                    </th>
                    <th className="text-right py-2 px-2">
                      {t("tools.mortgage.balance")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(showFullSchedule
                    ? amortizationSchedule
                    : amortizationSchedule.slice(0, 12)
                  ).map((entry) => (
                    <tr
                      key={entry.month}
                      className="border-b border-neutral-100"
                    >
                      <td className="py-2 px-2">{entry.month}</td>
                      <td className="text-right py-2 px-2">
                        {formatCurrency(entry.payment)}
                      </td>
                      <td className="text-right py-2 px-2">
                        {formatCurrency(entry.principal)}
                      </td>
                      <td className="text-right py-2 px-2">
                        {formatCurrency(entry.interest)}
                      </td>
                      <td className="text-right py-2 px-2">
                        {formatCurrency(entry.balance)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Export Button */}
      {monthlyPayment && (
        <div className="text-center mb-10 mt-8">
          <Button
            variant="ghost"
            className={`inline-flex items-center font-medium ${
              !monthlyPayment || subscriptionLoading || subscriptionError
                ? "text-neutral-400 cursor-not-allowed"
                : "text-neutral-700 hover:text-[#EA580C] cursor-pointer"
            }`}
            onClick={handleExportResults}
            disabled={!monthlyPayment || subscriptionLoading || !!subscriptionError}
          >
            <Download className="mr-1 h-4 w-4" />
            {subscriptionLoading ? t("common.loading") : t("common.exportResults")}
            {!user && !subscriptionLoading && (
              <span className="ml-1 text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full">
                {t("common.loginRequired")}
              </span>
            )}
            {user && !isSubscribed && !subscriptionLoading && !subscriptionError && (
              <span className="ml-1 text-xs bg-orange-500 text-white px-2 py-0.5 rounded-full">
                {t("common.subscriptionRequired")}
              </span>
            )}
            {subscriptionError && (
              <span className="ml-1 text-xs bg-red-500 text-white px-2 py-0.5 rounded-full">
                Error
              </span>
            )}
          </Button>
          {subscriptionError && (
            <p className="text-xs text-red-500 mt-2">
              Error verificando suscripción. Inténtalo de nuevo.
            </p>
          )}
        </div>
      )}

      {/* Educational Content Section */}
      <div className="mt-16 space-y-12">
        {/* How it works */}
        <div className="prose max-w-none">
          <h2 className="text-3xl font-bold text-neutral-900 mb-6">
            {t("tools.mortgage.howItWorksTitle")}
          </h2>
          <div className="bg-white rounded-lg p-8 border border-neutral-200 shadow-sm">
            <p className="text-lg text-neutral-700 leading-relaxed mb-6">
              {t("tools.mortgage.howItWorksDesc1")}
            </p>
            <p className="text-lg text-neutral-700 leading-relaxed mb-6">
              {t("tools.mortgage.howItWorksDesc2")}
            </p>
            <p className="text-lg text-neutral-700 leading-relaxed mb-6">
              {t("tools.mortgage.howItWorksDesc3")}
            </p>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 my-6">
              <p className="text-neutral-800 font-medium">
                {t("tools.mortgage.howItWorksExample")}
              </p>
            </div>
          </div>
        </div>

        {/* Formula Section */}
        <div className="prose max-w-none">
          <h2 className="text-3xl font-bold text-neutral-900 mb-6">
            {t("tools.mortgage.formulaTitle")}
          </h2>
          <div className="relative bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 rounded-xl p-8 border border-slate-200">
            {/* Formula Box */}
            <div className="relative bg-white rounded-xl p-8 mb-8 text-center shadow-lg border-2 border-[#EA580C]">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#EA580C] text-white px-4 py-1 rounded-full text-sm font-semibold">
                {t("tools.mortgage.formulaTitle")}
              </div>
              <p className="text-3xl font-mono font-bold text-neutral-900 tracking-wide">
                M = P × [r(1 + r)<sup className="text-lg">n</sup>] / [(1 + r)<sup className="text-lg">n</sup> - 1]
              </p>
            </div>
            
            {/* Variables Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-white rounded-lg p-4 border border-slate-200 hover:border-[#EA580C] transition-colors">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                    <span className="font-mono font-bold text-white text-sm">M</span>
                  </div>
                  <p className="text-sm font-semibold text-neutral-900">
                    {t("tools.mortgage.formulaM")}
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 border border-slate-200 hover:border-[#EA580C] transition-colors">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                    <span className="font-mono font-bold text-white text-sm">P</span>
                  </div>
                  <p className="text-sm font-semibold text-neutral-900">
                    {t("tools.mortgage.formulaP")}
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 border border-slate-200 hover:border-[#EA580C] transition-colors">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <span className="font-mono font-bold text-white text-sm">r</span>
                  </div>
                  <p className="text-sm font-semibold text-neutral-900">
                    {t("tools.mortgage.formulaR")}
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 border border-slate-200 hover:border-[#EA580C] transition-colors">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-[#EA580C] to-[#FF7700] rounded-lg flex items-center justify-center">
                    <span className="font-mono font-bold text-white text-sm">n</span>
                  </div>
                  <p className="text-sm font-semibold text-neutral-900">
                    {t("tools.mortgage.formulaN")}
                  </p>
                </div>
              </div>
            </div>

            {/* Example Box */}
            <div className="bg-gradient-to-r from-[#EA580C]/10 via-orange-50/50 to-[#EA580C]/10 rounded-lg p-6 border-l-4 border-[#EA580C]">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 bg-[#EA580C] rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-xs font-bold">💡</span>
                </div>
                <p className="text-neutral-800 leading-relaxed">
                  {t("tools.mortgage.formulaExample")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Types of Mortgages */}
        <div className="prose max-w-none">
          <h2 className="text-3xl font-bold text-neutral-900 mb-6">
            {t("tools.mortgage.typesTitle")}
          </h2>
          <p className="text-lg text-neutral-700 leading-relaxed mb-8">
            {t("tools.mortgage.typesIntro")}
          </p>
          <div className="space-y-6">
            <div className="bg-white rounded-lg p-6 border border-neutral-200 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <Home className="w-6 h-6 text-[#EA580C]" />
                <h3 className="text-2xl font-bold text-neutral-900">
                  {t("tools.mortgage.fixedRateTitle")}
                </h3>
              </div>
              <p className="text-neutral-700 leading-relaxed mb-4">
                {t("tools.mortgage.fixedRateDesc")}
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-green-50 rounded-lg p-4 border-l-4 border-green-500">
                  <p className="font-semibold text-green-900 mb-2">
                    {t("tools.mortgage.fixedRatePros")}
                  </p>
                </div>
                <div className="bg-red-50 rounded-lg p-4 border-l-4 border-red-500">
                  <p className="font-semibold text-red-900 mb-2">
                    {t("tools.mortgage.fixedRateCons")}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 border border-neutral-200 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <TrendingUp className="w-6 h-6 text-[#EA580C]" />
                <h3 className="text-2xl font-bold text-neutral-900">
                  {t("tools.mortgage.variableRateTitle")}
                </h3>
              </div>
              <p className="text-neutral-700 leading-relaxed mb-4">
                {t("tools.mortgage.variableRateDesc")}
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-green-50 rounded-lg p-4 border-l-4 border-green-500">
                  <p className="font-semibold text-green-900 mb-2">
                    {t("tools.mortgage.variableRatePros")}
                  </p>
                </div>
                <div className="bg-red-50 rounded-lg p-4 border-l-4 border-red-500">
                  <p className="font-semibold text-red-900 mb-2">
                    {t("tools.mortgage.variableRateCons")}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 border border-neutral-200 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <DollarSign className="w-6 h-6 text-[#EA580C]" />
                <h3 className="text-2xl font-bold text-neutral-900">
                  {t("tools.mortgage.hybridTitle")}
                </h3>
              </div>
              <p className="text-neutral-700 leading-relaxed">
                {t("tools.mortgage.hybridDesc")}
              </p>
            </div>
          </div>
        </div>

        {/* Tips for Getting Best Mortgage */}
        <div className="prose max-w-none">
          <h2 className="text-3xl font-bold text-neutral-900 mb-6">
            {t("tools.mortgage.tipsTitle")}
          </h2>
          <p className="text-lg text-neutral-700 leading-relaxed mb-8">
            {t("tools.mortgage.tipsIntro")}
          </p>
          <div className="space-y-5">
            {[1, 2, 3, 4, 5, 6].map((num) => (
              <div key={num} className="flex items-center gap-4">
                <div className="w-7 h-7 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">✓</span>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-neutral-900 mb-2">
                    {t(`tools.mortgage.tip${num}Title`)}
                  </h4>
                  <p className="text-neutral-700 leading-relaxed">
                    {t(`tools.mortgage.tip${num}Desc`)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Common Mistakes */}
        <div className="prose max-w-none">
          <h2 className="text-3xl font-bold text-neutral-900 mb-6">
            {t("tools.mortgage.mistakesTitle")}
          </h2>
          <p className="text-lg text-neutral-700 leading-relaxed mb-8">
            {t("tools.mortgage.mistakesIntro")}
          </p>
          <div className="space-y-6">
            {[1, 2, 3, 4, 5, 6].map((num) => (
              <div key={num} className="bg-red-50 rounded-lg p-6 border-l-4 border-red-500">
                <h4 className="text-lg font-bold text-red-900 mb-3">
                  ❌ {t(`tools.mortgage.mistake${num}Title`)}
                </h4>
                <p className="text-neutral-700 leading-relaxed">
                  {t(`tools.mortgage.mistake${num}Desc`)}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Real Examples */}
        <div className="prose max-w-none">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
              {t("tools.mortgage.examplesTitle")}
            </h2>
            <p className="text-lg text-neutral-600 leading-relaxed max-w-3xl mx-auto">
              {t("tools.mortgage.examplesIntro")}
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { num: 1, gradient: "from-blue-500 to-cyan-500", icon: Home },
              { num: 2, gradient: "from-purple-500 to-pink-500", icon: TrendingUp },
              { num: 3, gradient: "from-orange-500 to-red-500", icon: DollarSign },
              { num: 4, gradient: "from-green-500 to-emerald-500", icon: Calculator }
            ].map(({ num, gradient, icon: Icon }) => (
              <div 
                key={num} 
                className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
              >
                {/* Gradient Header */}
                <div className={`h-2 bg-gradient-to-r ${gradient}`} />
                
                {/* Content */}
                <div className="p-6 pt-6">
                  <div className="flex items-start gap-4 mb-4">
                    {/* Number Badge */}
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-r ${gradient} flex items-center justify-center shadow-md`}>
                      <span className="text-white font-bold text-lg">{num}</span>
                    </div>
                    
                    {/* Title */}
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-neutral-900 group-hover:text-[#EA580C] transition-colors">
                        {t(`tools.mortgage.example${num}Title`)}
                      </h4>
                    </div>
                  </div>
                  
                  {/* Structured Content */}
                  <div className="space-y-3 pl-14">
                    {/* Parse and display key-value pairs */}
                    {t(`tools.mortgage.example${num}Desc`).split('|').map((item, idx) => {
                      const trimmedItem = item.trim();
                      if (!trimmedItem) return null;
                      
                      // Check if it's a key-value pair
                      if (trimmedItem.includes(':')) {
                        const [key, value] = trimmedItem.split(':').map(s => s.trim());
                        const hasNumber = /\d/.test(value);
                        
                        return (
                          <div key={idx} className="flex items-start justify-between gap-3 text-sm">
                            <span className="text-neutral-500 font-medium flex-shrink-0">
                              {key}:
                            </span>
                            <span className={`text-right font-semibold ${hasNumber ? `bg-gradient-to-r ${gradient} bg-clip-text text-transparent` : 'text-neutral-700'}`}>
                              {value}
                            </span>
                          </div>
                        );
                      }
                      
                      // If it's a strategy or note (no colon), display as accent box
                      return (
                        <div key={idx} className={`mt-4 p-3 rounded-lg bg-gradient-to-r ${gradient} bg-opacity-10 border-l-4 border-${gradient.split(' ')[1]}`}>
                          <p className="text-xs font-medium text-neutral-700 leading-relaxed">
                            {trimmedItem}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Decorative Element */}
                <div className={`absolute bottom-0 right-0 w-32 h-32 bg-gradient-to-br ${gradient} opacity-5 rounded-tl-full transform translate-x-8 translate-y-8 group-hover:scale-150 transition-transform duration-700`} />
              </div>
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="prose max-w-none">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
              {t("tools.mortgage.faqTitle")}
            </h2>
            <p className="text-lg text-neutral-600">
              {t("tools.mortgage.faqSubtitle") || "Encuentra respuestas a las preguntas más comunes sobre hipotecas"}
            </p>
          </div>
          <div className="space-y-4 max-w-4xl mx-auto">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => {
              const isOpen = openFaqIndex === num;
              return (
                <div 
                  key={num} 
                  className="bg-white rounded-xl border-2 border-neutral-200 overflow-hidden transition-all duration-300 hover:border-[#EA580C]/30 hover:shadow-lg"
                >
                  {/* Question - Clickable Header */}
                  <button
                    onClick={() => setOpenFaqIndex(isOpen ? null : num)}
                    className="w-full flex items-center justify-between gap-4 p-6 text-left group"
                  >
                    <div className="flex items-start gap-4 flex-1">
                      {/* Number Badge */}
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 ${
                        isOpen 
                          ? 'bg-gradient-to-r from-[#EA580C] to-orange-600 text-white shadow-lg scale-110' 
                          : 'bg-neutral-100 text-neutral-600 group-hover:bg-[#EA580C]/10 group-hover:text-[#EA580C]'
                      }`}>
                        {num}
                      </div>
                      
                      {/* Question Text */}
                      <h4 className={`text-lg font-bold transition-colors duration-300 ${
                        isOpen 
                          ? 'text-[#EA580C]' 
                          : 'text-neutral-900 group-hover:text-[#EA580C]'
                      }`}>
                        {t(`tools.mortgage.faq${num}Q`)}
                      </h4>
                    </div>
                    
                    {/* Chevron Icon */}
                    <ChevronDown 
                      className={`flex-shrink-0 h-6 w-6 transition-all duration-300 ${
                        isOpen 
                          ? 'rotate-180 text-[#EA580C]' 
                          : 'text-neutral-400 group-hover:text-[#EA580C]'
                      }`}
                    />
                  </button>
                  
                  {/* Answer - Expandable Content */}
                  <div 
                    className={`transition-all duration-300 ease-in-out ${
                      isOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-6 pb-6 pl-[4.5rem]">
                      <div className="pt-2 border-t border-neutral-100">
                        <p className="text-neutral-700 leading-relaxed pt-4">
                          {t(`tools.mortgage.faq${num}A`)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Related Content Section */}
        <div className="mt-16 space-y-12">
          {/* Other Calculators Section */}
          <div className="bg-gradient-to-br from-neutral-50 to-white rounded-xl p-8 border border-neutral-200">
            <div className="flex items-center gap-2 mb-6">
              <Calculator className="h-6 w-6 text-[#EA580C]" />
              <h2 className="text-2xl font-bold text-neutral-900">
                {t("tools.compound.otherCalculators")}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <LanguageLink
                to="/tools/compound-interest"
                className="group p-4 bg-white rounded-lg border border-neutral-200 hover:border-[#EA580C] hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-neutral-900 group-hover:text-[#EA580C] transition-colors">
                      {t("tools.compound.compoundInterestCalculator")}
                    </h3>
                    <p className="text-sm text-neutral-600 mt-1">
                      {t("tools.compound.compoundInterestDesc")}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-neutral-400 group-hover:text-[#EA580C] group-hover:translate-x-1 transition-all" />
                </div>
              </LanguageLink>

              <LanguageLink
                to="/tools/monthly-savings-calculator"
                className="group p-4 bg-white rounded-lg border border-neutral-200 hover:border-[#EA580C] hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-neutral-900 group-hover:text-[#EA580C] transition-colors">
                      {t("tools.compound.monthlySavingsCalculator")}
                    </h3>
                    <p className="text-sm text-neutral-600 mt-1">
                      {t("tools.compound.monthlySavingsDesc")}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-neutral-400 group-hover:text-[#EA580C] group-hover:translate-x-1 transition-all" />
                </div>
              </LanguageLink>

              <LanguageLink
                to="/tools/net-salary"
                className="group p-4 bg-white rounded-lg border border-neutral-200 hover:border-[#EA580C] hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-neutral-900 group-hover:text-[#EA580C] transition-colors">
                      {t("tools.compound.salaryCalculator")}
                    </h3>
                    <p className="text-sm text-neutral-600 mt-1">
                      {t("tools.compound.salaryDesc")}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-neutral-400 group-hover:text-[#EA580C] group-hover:translate-x-1 transition-all" />
                </div>
              </LanguageLink>

              <LanguageLink
                to="/tools/salary-comparison"
                className="group p-4 bg-white rounded-lg border border-neutral-200 hover:border-[#EA580C] hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-neutral-900 group-hover:text-[#EA580C] transition-colors">
                      {t("tools.compound.salaryComparisonCalculator")}
                    </h3>
                    <p className="text-sm text-neutral-600 mt-1">
                      {t("tools.compound.salaryComparisonDesc")}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-neutral-400 group-hover:text-[#EA580C] group-hover:translate-x-1 transition-all" />
                </div>
              </LanguageLink>
            </div>
          </div>

          {/* Related Articles Section */}
          <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-8 border border-blue-200">
            <div className="flex items-center gap-2 mb-6">
              <TrendingUp className="h-6 w-6 text-[#EA580C]" />
              <h2 className="text-2xl font-bold text-neutral-900">
                {t("tools.compound.relatedArticles")}
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* First Article - Takes 2 columns */}
              <LanguageLink
                to="/resources/renting-vs-buying-article"
                className="md:col-span-2 group relative rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 h-[280px]"
              >
                <img 
                  src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=80&w=800&auto=format&fit=crop" 
                  alt="Modern house keys on table with mortgage documents and calculator"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/20 group-hover:from-black/80 group-hover:via-black/40 transition-all duration-500" />
                <div className="absolute inset-0 p-6 flex flex-col justify-end">
                  <div className="transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-2 leading-tight">
                      {t("resources.article7.title")}
                    </h3>
                    <p className="text-white/90 mb-3 text-sm leading-relaxed line-clamp-2">
                      {t("resources.article7.description")}
                    </p>
                    <span className="inline-flex items-center text-xs font-semibold text-white bg-[#EA580C] px-3 py-1.5 rounded-lg group-hover:bg-[#DD5500] transition-colors">
                      {t("tools.compound.readMore")}
                      <ArrowRight className="h-3 w-3 ml-2 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </div>
              </LanguageLink>

              {/* Second Article */}
              <LanguageLink
                to="/resources/mortgage-renegotiation-article"
                className="group relative block rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 h-[280px]"
              >
                <img 
                  src="https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?q=80&w=500&auto=format&fit=crop" 
                  alt="Mortgage renegotiation and home finance concept"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent group-hover:from-black/70 transition-all duration-500" />
                <div className="absolute inset-0 p-5 flex flex-col justify-end">
                  <h3 className="text-lg font-bold text-white mb-2 leading-tight">
                    {t("resources.article12.title")}
                  </h3>
                  <span className="inline-flex items-center self-start text-xs font-semibold text-white bg-[#EA580C] px-3 py-1.5 rounded-lg group-hover:bg-[#DD5500] transition-colors">
                    {t("tools.compound.readMore")}
                    <ArrowRight className="h-3 w-3 ml-2 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </LanguageLink>

              {/* Third Article */}
              <LanguageLink
                to="/resources/monthly-saving-challenges-article"
                className="group relative block rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 h-[280px]"
              >
                <img 
                  src="https://images.unsplash.com/photo-1579621970588-a35d0e7ab9b6?q=80&w=500&auto=format&fit=crop" 
                  alt="Monthly saving challenges with piggy bank and calendar"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent group-hover:from-black/70 transition-all duration-500" />
                <div className="absolute inset-0 p-5 flex flex-col justify-end">
                  <h3 className="text-lg font-bold text-white mb-2 leading-tight">
                    {t("resources.article10.title")}
                  </h3>
                  <span className="inline-flex items-center self-start text-xs font-semibold text-white bg-[#EA580C] px-3 py-1.5 rounded-lg group-hover:bg-[#DD5500] transition-colors">
                    {t("tools.compound.readMore")}
                    <ArrowRight className="h-3 w-3 ml-2 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </LanguageLink>

              {/* Fourth Article - Spans remaining space */}
              <LanguageLink
                to="/resources/start-investing-article"
                className="md:col-span-2 lg:col-span-4 group relative rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 h-[200px]"
              >
                <img 
                  src="https://images.unsplash.com/photo-1579532537598-459ecdaf39cc?q=80&w=800&auto=format&fit=crop" 
                  alt="Young professional starting investment journey with laptop showing financial dashboard and growth charts"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-black/30 group-hover:from-black/80 group-hover:via-black/50 transition-all duration-500" />
                <div className="absolute inset-0 p-6 flex flex-col justify-center items-start max-w-2xl">
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-2 leading-tight">
                    {t("tools.compound.article1Title")}
                  </h3>
                  <p className="text-white/90 mb-3 text-sm leading-relaxed line-clamp-2">
                    {t("tools.compound.article1Desc")}
                  </p>
                  <span className="inline-flex items-center text-xs font-semibold text-white bg-[#EA580C] px-3 py-1.5 rounded-lg group-hover:bg-[#DD5500] transition-colors">
                    {t("tools.compound.readMore")}
                    <ArrowRight className="h-3 w-3 ml-2 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </LanguageLink>
            </div>
          </div>
        </div>
      </div>

      {/* Authentication Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        mode={authMode}
        onModeChange={setAuthMode}
      />
    </div>
  );
};

export default MortgageCalculator;

